import { useState, useEffect } from 'react';
import { differenceInCalendarDays } from 'date-fns';

export function useDeadlineReminder(items, contextKey, type = 'task') {
    const [isTodayOpen, setIsTodayOpen] = useState(false);
    const [isWeekOpen, setIsWeekOpen] = useState(false);
    const [todayItems, setTodayItems] = useState([]);
    const [weekItems, setWeekItems] = useState([]);

    // Legacy compatibility
    const [isOpen, setIsOpen] = useState(false);
    const [urgentItems, setUrgentItems] = useState([]);

    const getItemDateTime = (item) => {
        let dateStr = null;
        let timeStr = '23:59';

        if (type === 'task') {
            dateStr = item.dataFim;
            timeStr = '23:59'; // Tasks don't have specific times
        } else if (type === 'event') {
            const d = item.start instanceof Date ? item.start : new Date(item.start);
            if (!isNaN(d)) dateStr = d.toISOString().split('T')[0];
            timeStr = item.horario || item.time || '23:59';
        }

        return { dateStr, timeStr };
    };

    const checkDeadlines = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const todayFiltered = [];
        const weekFiltered = [];

        items.forEach(item => {
            const { dateStr, timeStr } = getItemDateTime(item);

            if (!dateStr) return;
            if (type === 'task' && item.status === 'Feito') return;
            if (type === 'event' && (item.status === 'Feito' || item.status === 'Não Realizado')) return;

            const itemDate = new Date(dateStr + 'T00:00:00');
            const diff = differenceInCalendarDays(itemDate, today);

            if (diff === 0) {
                // Today items
                todayFiltered.push({ ...item, _sortTime: timeStr, _diff: diff });
            } else if (diff >= 1 && diff <= 6) {
                // This week (excluding today), up to 6 days ahead
                weekFiltered.push({ ...item, _sortTime: timeStr, _diff: diff });
            }
        });

        // Sort today items by time (closest first)
        todayFiltered.sort((a, b) => a._sortTime.localeCompare(b._sortTime));

        // Sort week items by date+time (closest first)
        weekFiltered.sort((a, b) => {
            if (a._diff !== b._diff) return a._diff - b._diff;
            return a._sortTime.localeCompare(b._sortTime);
        });

        setTodayItems(todayFiltered);
        setWeekItems(weekFiltered);

        // Legacy: combine for backward compat
        const allUrgent = [...todayFiltered, ...weekFiltered];
        setUrgentItems(allUrgent);

        return { todayFiltered, weekFiltered };
    };

    // Auto-check on load (only once a day)
    useEffect(() => {
        if (items.length > 0) {
            const todayStr = new Date().toISOString().split('T')[0];
            const dismissedKey = `reminder_dismissed_${contextKey}_${todayStr}`;
            const isDismissed = localStorage.getItem(dismissedKey) === 'true';

            if (!isDismissed) {
                const { todayFiltered, weekFiltered } = checkDeadlines();
                // Show today popup first if there are items
                if (todayFiltered.length > 0) {
                    setIsTodayOpen(true);
                    setIsOpen(true); // legacy
                } else if (weekFiltered.length > 0) {
                    setIsWeekOpen(true);
                    setIsOpen(true); // legacy
                }
            } else {
                // Still calculate items for manual button
                checkDeadlines();
            }
        }
    }, [items, contextKey, type]);

    // When today popup closes, show week popup if there are items
    const closeTodayPopup = () => {
        setIsTodayOpen(false);
        if (weekItems.length > 0) {
            setIsWeekOpen(true);
        } else {
            setIsOpen(false); // legacy
        }
    };

    const closeWeekPopup = () => {
        setIsWeekOpen(false);
        setIsOpen(false); // legacy
    };

    const dismissToday = () => {
        const todayStr = new Date().toISOString().split('T')[0];
        const dismissedKey = `reminder_dismissed_${contextKey}_${todayStr}`;
        localStorage.setItem(dismissedKey, 'true');
        setIsTodayOpen(false);
        setIsWeekOpen(false);
        setIsOpen(false); // legacy
    };

    const openReminderManually = () => {
        checkDeadlines();
        // Always show today first if available, else week
        if (todayItems.length > 0 || items.some(item => {
            const { dateStr } = getItemDateTime(item);
            if (!dateStr) return false;
            const diff = differenceInCalendarDays(new Date(dateStr + 'T00:00:00'), new Date().setHours(0, 0, 0, 0));
            return diff === 0;
        })) {
            // Recalculate to be safe
            const { todayFiltered, weekFiltered } = checkDeadlines();
            if (todayFiltered.length > 0) {
                setIsTodayOpen(true);
            } else if (weekFiltered.length > 0) {
                setIsWeekOpen(true);
            } else {
                // Show empty today popup
                setIsTodayOpen(true);
            }
        } else {
            const { weekFiltered } = checkDeadlines();
            if (weekFiltered.length > 0) {
                setIsWeekOpen(true);
            } else {
                setIsTodayOpen(true); // Show "nothing today" message
            }
        }
        setIsOpen(true); // legacy
    };

    return {
        // New API
        isTodayOpen,
        isWeekOpen,
        todayItems,
        weekItems,
        closeTodayPopup,
        closeWeekPopup,
        dismissToday,
        openReminderManually,
        // Legacy API (backward compat)
        isOpen,
        setIsOpen,
        urgentItems,
    };
}
